import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Input } from "../components/ui/Input.jsx";
import { Button } from "../components/ui/Button.jsx";
import * as authApi from "../api/auth.api.js";

const schema = z
  .object({
    password: z.string().min(8, "At least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [serverError, setServerError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <h1 className="font-display text-2xl text-text-primary">Reset link missing</h1>
          <p className="mt-2 text-sm text-text-secondary">
            Open the link from your email — it includes the token this page needs.
          </p>
          <Link
            to="/forgot-password"
            className="mt-6 inline-block text-sm text-accent hover:text-accent-hover"
          >
            Request a new link
          </Link>
        </div>
      </main>
    );
  }

  const onSubmit = async ({ password }) => {
    setServerError(null);
    try {
      await authApi.resetPassword({ token, password });
      toast.success("Password reset — sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      setServerError(err?.response?.data?.message ?? "Reset failed — link may have expired");
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-bg-base">
            <KeyRound className="h-5 w-5" />
          </div>
          <h1 className="font-display text-3xl text-text-primary">Set a new password</h1>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-5 rounded-xl border border-border-subtle bg-bg-elevated p-6 shadow-card"
          noValidate
        >
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            error={errors.password?.message}
            {...register("password")}
          />
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.confirmPassword?.message}
            {...register("confirmPassword")}
          />
          {serverError && <p className="text-sm text-danger">{serverError}</p>}
          <Button type="submit" className="w-full" loading={isSubmitting}>
            Reset password
          </Button>
        </form>
      </div>
    </main>
  );
}
